/*
 * Поля ввода. Геометрия из дизайн-системы «Изнутри», состояния — из макета
 * «Оболочка - этап 0», карточка «Поля · включая маскированное».
 */

.group {
  display: flex;
  flex-direction: column;
  gap: 5px;
  /* Поле стоит в колонке формы; без этого длинная подпись растягивает колонку,
     вместо того чтобы перенестись на вторую строку. */
  min-width: 0;
}

.label {
  font-size: 12px;
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  /* Немецкие подписи полей — самые длинные строки в форме. */
  text-wrap: pretty;
}

.required {
  color: var(--danger);
}

.hint {
  font-size: var(--font-size-sm);
  line-height: 1.45;
  color: var(--text-secondary);
  text-wrap: pretty;
}

.error {
  font-size: var(--font-size-sm);
  line-height: 1.45;
  color: var(--danger-hover);
  text-wrap: pretty;
}

/* ─────────── сам ввод ─────────── */

.control {
  width: 100%;
  font-family: var(--font-ui);
  font-size: var(--font-size-base);
  color: var(--text-primary);
  background: var(--bg-card);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  padding: 8px 11px;
  transition: border-color 0.15s, box-shadow 0.15s;
}

@media (pointer: coarse) {
  /* У поля область нажатия наращивается высотой, а не невидимой накладкой:
     накладка поверх поля перехватила бы установку курсора в текст. */
  .control {
    min-height: var(--touch-min-target);
  }
}

.control::placeholder {
  color: var(--text-faint);
}

/*
 * ФОКУС.
 * Кольцо дизайн-системы (sage + мягкая подсветка) оставлено, но оно НЕ единственный
 * признак: сверху ложится общее кольцо фокуса из base.css. Отступ увеличен до 4 px,
 * чтобы два кольца не наезжали друг на друга и оба читались.
 */
.control:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: var(--field-focus-ring);
}

.control:focus-visible {
  outline: var(--focus-ring-width) solid var(--focus-ring-color);
  outline-offset: 4px;
}

.control:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.control:read-only {
  /* В макете это #f7f4ed. Взят существующий токен рабочей области: разница
     в три единицы, зато ни одного нового цвета и работает в обеих темах. */
  background: var(--bg-workspace);
  color: var(--text-secondary);
}

.control[aria-invalid="true"] {
  border-color: var(--danger);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--danger) 16%, transparent);
}

/* Выпадающий список: своя стрелка, потому что системная в тёмной теме остаётся
   светлой и выглядит чужой. Цвет стрелки — currentColor через маску, а не картинка
   с вшитым цветом. */
.select {
  appearance: none;
  padding-right: 32px;
  cursor: pointer;
}

.selectWrap {
  position: relative;
  display: flex;
  min-width: 0;
}

.selectArrow {
  position: absolute;
  top: 50%;
  right: 10px;
  translate: 0 -50%;
  width: 12px;
  height: 12px;
  pointer-events: none;
  color: var(--text-secondary);
}

.textarea {
  min-height: 70px;
  resize: vertical;
}

/* ─────────── маскированное поле ─────────── */

.maskedRow {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
}

.masked {
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.04em;
  /* Номер телефона не переносится никогда: разорванный номер читают неверно. */
  white-space: nowrap;
}

/* ─────────── флажок и выключатель ─────────── */

.checkRow {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: var(--font-size-base);
  line-height: 1.4;
  cursor: pointer;
  user-select: none;
  min-width: 0;
  text-wrap: pretty;
}

@media (pointer: coarse) {
  /* Видимый квадрат остаётся 17 px — растёт строка, и нажатие по строке
     переключает флажок родным поведением тега label. */
  .checkRow {
    min-height: var(--touch-min-target);
  }
}

.checkbox {
  appearance: none;
  flex: none;
  width: 17px;
  height: 17px;
  margin: 0;
  border: 1.5px solid var(--border-strong);
  border-radius: var(--radius-sm);
  background: var(--bg-card);
  cursor: pointer;
  position: relative;
  transition: background 0.15s, border-color 0.15s;
}

.checkbox:checked {
  background: var(--accent);
  border-color: var(--accent);
}

.checkbox:checked::after {
  content: "";
  position: absolute;
  left: 4.5px;
  top: 1.5px;
  width: 4px;
  height: 8px;
  border: solid var(--text-on-accent);
  border-width: 0 2px 2px 0;
  rotate: 45deg;
}

.checkbox:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.switch {
  appearance: none;
  flex: none;
  position: relative;
  width: 36px;
  height: 21px;
  margin: 0;
  border: none;
  border-radius: var(--radius-pill);
  background: var(--border-strong);
  cursor: pointer;
  transition: background 0.15s;
}

.switch::after {
  content: "";
  position: absolute;
  top: 2.5px;
  left: 2.5px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--switch-knob);
  box-shadow: var(--shadow-card);
  transition: translate 0.15s;
}

.switch:checked {
  background: var(--accent);
}

.switch:checked::after {
  translate: 15px 0;
}

.switch:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

@media (prefers-reduced-motion: reduce) {
  .switch::after {
    transition: none;
  }
}

/* Многострочное поле (TextAreaField): высота — под три строки описания, как в макете 3-Б7. */
.textarea {
  min-height: 64px;
  resize: vertical;
  line-height: 1.5;
}
